<template>
    <div class="content-container">
        <div class="top-content">
            <form @submit.prevent>
                <div class="flex-row-centered">
                        <MarketSelector  enableSE @change=updateMarketSelection />
                         <div class="flex-col-centered selection">
                        <label for="owner"><h3 class="header">Owner:</h3></label>
                        <select v-model="ownerSelection" class="dropdown" required id="owner" @change="getTableData()">
                            <option v-for="contractOwner in contractOwners"
                                :value="contractOwner.OwnerId" 
                                :key="contractOwner.OwnerId" >
                                {{ contractOwner.FullName }}
                            </option>
                        </select>
                    </div>
                    <div class="flex-col-centered">
                        <button class="submit-btn" 
                            @click="insurancePopUpStatus=!insurancePopUpStatus">
                            New Claim
                        </button>
                    </div>
                </div>
            </form>
        </div>
        <div class=link-btn>
            [<a @click="expandedCols = !expandedCols"> {{ expandedCols ? "Collapse Rows" : "Expand Rows" }}</a>]
        </div>
        <Table 
            :Rows=insuranceRows 
            :Cols=cols 
            :Labels=labels 
            SplitColKeys
            :DefaultSortCols=defaultSortCols
            :DefaultSortOrders=defaultSortOrders
        >
            <template #isCompleted-1={value}>
                <button class="table-button" :disabled="value.isCompleted"
                    @click="displayEditPopUp(value)">
                        Edit
                </button>
            </template>
            <template #isCompleted={value}>
                <TickCrossBooleanDisplay :value="value.isCompleted" />
            </template>
            <template #comments="props">
                <div style="display: flex;" v-if="props.value.comments">
                    <button 
                        class="overflow-text"
                        @click="ViewComment(props.value)"
                        :title="`View Full Comment in Popup`"
                    >
                        {{ props.value.comments }}
                        </button>
                </div>
                <div style="display: flex;" v-if="props.value.comments == null">
                    <button
                        class="table-button"
                        @click="ViewComment(props.value)"
                    >
                        Add Comment
                        </button>
                </div>
            </template>
        </Table>
        <InsuranceClaimsPopUp 
            v-model=insurancePopUpStatus 
            :disableDefaultToolTip=true
            :market="marketSelection"
            @confirm="getTableData"
        />
        <InsuranceClaimsEditPopUp
            v-model=selectedRow
            :disableDefaultToolTip=true
            @confirm="getTableData"
        />
        <InsuranceCommentPopUp
            v-model=commentPopUpContent
            @close="newCommentInsertedPopUp=false"
            @confirm="getTableData"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
// Services
import { insuranceClaims } from "@/services/network";
import InsuranceClaimsPopUp from "@/components/InsuranceClaimsPopUp.vue"
import InsuranceClaimsEditPopUp from "@/components/InsuranceClaimsEditPopUp.vue";
import InsuranceCommentPopUp from "@/components/InsuranceCommentPopUp.vue";
import IInsuranceClaimsGetQuery from "@/Interfaces/InsuranceClaims/IInsuranceClaimsGetQuery"
import { contractOwners as contractOwnersNetworkService } from "@/services/network";
// Components
import MarketSelector from '@/components/MarketSelector.vue';
// Misc
import { DisplayableKeys } from "@/services/SorterService";
import { marketSelection, ownerSelection } from "@/services/helpers/Helper";
import useNotifierWithErrFormatter from "@/services/composables/NotifierWithErrFormatter";

// Table
import Table from "@/components/TablePersistentChecking.vue";
import DateIgnoreTime from "@/types/DateIgnoreTime";
import TickCrossBooleanDisplay from "@/components/TickCrossBooleanDisplay.vue";
import IInsuranceForm from "@/Interfaces/InsuranceClaims/IInsuranceClaimsForm";
import { ContractOwner } from "@/models/CarPurchases/ContractOwner";

const contractOwners = ref<ContractOwner[]>([]);
const unfilteredContractOwners = ref<ContractOwner[]>([]);
const defaultSortOrders = ref<(1|-1)[]>([-1]);
const defaultSortCols = ref([4]);
const expandedCols = ref(false)
const colsExpanded: DisplayableKeys<IInsuranceForm>[] = ["contractId", "vin", "registrationNumber", "isCompleted", "ticketId", "paymentDate", "caseReferenceId", "fullAmount", "excessAmount", "companyName", "companyVatId", "companyLocation", "comments", "userEmail", "isCompleted"];
const labelsExpanded = [ "Contract ID", "VIN", "Registration Number", "Completed", "Ticket ID", "Payment Date", "Case Reference ID", "Full Amount", "Excess Amount", "Company Name", "Company VAT ID", "Company Location", "Comments", "Reporter", "Edit"]
const colsNormal: DisplayableKeys<IInsuranceForm>[] = ["contractId", "vin", "registrationNumber", "isCompleted", "ticketId", "paymentDate", "fullAmount", "excessAmount", "comments", "userEmail", "isCompleted"];
const labelsNormal = [ "Contract ID", "VIN", "Registration Number", "Completed", "Ticket ID", "Payment Date", "Full Amount", "Excess Amount", "Comments", "Reporter", "Edit"]
const cols = computed(() => {return  expandedCols.value ? colsExpanded : colsNormal; });
const labels = computed(() => {return  expandedCols.value ? labelsExpanded : labelsNormal; });
const working = ref(false);
const insuranceRows =ref<IInsuranceForm[]>();
const selectedRow = ref<IInsuranceForm>();
const insurancePopUpStatus = ref(false);
const commentPopUpContent = ref<IInsuranceForm>();
const newCommentInsertedPopUp = ref(false);

onMounted(async () => {
    document.title = "Insurance Claims - UV";
    insuranceRows.value = undefined;
    await GetContractOwners();
    await SetContractOwnerByMarket();
    await SetDefaultOwner();
    await getTableData()
});
async function updateMarketSelection(){
    await SetContractOwnerByMarket();
    await SetDefaultOwner();
    getTableData()
}
async function GetContractOwners() {
    try {
        const res = (await contractOwnersNetworkService.get()).data
        
        unfilteredContractOwners.value = res;
    } catch (error) {
        useNotifierWithErrFormatter().error({
            errorType: "Contract Owners",
            error,
        });
    }
}
async function SetContractOwnerByMarket(){
    contractOwners.value = unfilteredContractOwners.value.filter( x => x.Market == marketSelection.value).sort((a, b) => {
                const aId = a?.OwnerId ?? "";
                const bId = b?.OwnerId ?? "";
                return bId.localeCompare(aId);
            }); 
}
async function SetDefaultOwner(){
    try{
        ownerSelection.value = contractOwners.value[0].OwnerId ?? "";
    }
    catch{
        console.warn("Contract owners were not loaded");
    }
}
async function getTableData(){
    if(working.value) return;    
    try{
        working.value=true
        insuranceRows.value = undefined;
        const request: IInsuranceClaimsGetQuery = {
            market: marketSelection.value, 
            owner: ownerSelection.value
        }
        const response = await insuranceClaims.get(request);
        let responseData = response.data;
        responseData.map(x => x.paymentDate = new DateIgnoreTime(x.paymentDate))
        insuranceRows.value = responseData
    }catch (error) {
        useNotifierWithErrFormatter().error({error});
    }
    working.value=false
}
function displayEditPopUp(row: IInsuranceForm){
    selectedRow.value = row
}
function ViewComment(row: IInsuranceForm){
    commentPopUpContent.value = row;
}
</script>

<style scoped lang="scss">
.top-content {
    padding: 0.5rem;
}
.form-container {
    display: grid;
    grid-template-columns: auto auto auto;
    gap: 20px;
    justify-content: space-evenly;
    justify-items: center;
    align-items: start;
    >*{
        min-width: 10rem;
        width: 100%;
        >*{
            width: 100%;
        }
    }
    :deep(select) {
        width: 100%;
        margin-bottom: 10px;
    }
    :deep(input) {
        width: 100%;
        margin-bottom: 10px;
    }
}
.submit-btn {
    cursor: pointer;
    border-width: 0px;
    border-radius: var(--buttons-border-radius);
    background-color: var(--primary-color-lighter);
    padding: 10px;
    color: blue;
    font-size: larger;
    font-weight: bold;
    &:hover {
        color: white;
        background-color: var(--primary-color);
    }
}
.selection-form-btn {
    height: 2.3rem;
    border-color: var(--primary-color-dark);
    font-weight: bold;
    background-color: var(--primary-color-lighter);
    color: blue;
    cursor: pointer;

    &:hover {
        background-color: var(--primary-color);
        color: white;
    }
}
:deep(.flex-row-centered){
    align-items: end;
}
.overflow-text {
    white-space: nowrap;
    overflow: hidden; 
    text-overflow: ellipsis;
    max-width: 16vw;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}
.overflow-text:hover {
  cursor: pointer; 
  color: blue; 
  text-decoration: underline; 
}
.link-btn {
    margin-left: auto;
    color:  var(--primary-color)
}
.link-btn a:hover{
    cursor: pointer;
}

:deep(.content-container){
    row-gap: 2px;
}
</style>